import { useRef, useState } from "react";
import { Upload, FileText, CheckCircle2, ShieldCheck, ArrowDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n/i18n";

const HERO_IMG = "/img/registro-hero.jpg";

export const RegistroHero = ({ onCta }) => {
  const { t } = useI18n();
  return (
  <section className="relative overflow-hidden border-b border-slate-200 bg-white">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:grid-cols-12 lg:px-10 lg:py-20">
      <div className="lg:col-span-7">
        <p className="fade-up text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Universitat Politècnica de València</p>
        <h1 className="fade-up-1 mt-5 font-display text-4xl font-extrabold leading-[0.92] tracking-tight text-slate-950 sm:text-5xl lg:text-7xl" data-testid="registro-title">
          {t("FORO DE EMPLEO")}<br /><span className="text-upv">UPV 2027</span>
        </h1>
        <p className="fade-up-2 mt-6 font-mono text-lg font-bold tracking-[0.2em] text-slate-900 sm:text-xl">{t("12–13 MAYO 2027")}</p>
        <p className="fade-up-2 mt-5 max-w-xl text-base leading-relaxed text-slate-600">
          {t("Más de 125 empresas te esperan. Regístrate una vez y recibe tu credencial digital: un único código QR para acceder al recinto y compartir tu currículum en cada stand.")}
        </p>
        <button onClick={onCta} className="fade-up-3 mt-9 inline-flex items-center gap-3 rounded-full bg-upv px-8 py-4 font-display text-base font-bold tracking-wider text-white transition-colors hover:bg-upv-dark" data-testid="registro-cta-button">
          {t("INSCRÍBETE")} <ArrowDown className="h-4 w-4" />
        </button>
        <div className="fade-up-3 mt-12 flex flex-wrap gap-8">
          {[["+10.000", "participantes"], ["+125", "empresas"], ["2", "jornadas"]].map(([v, l]) => (
            <div key={l}><p className="font-display text-3xl font-bold">{v}</p><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t(l)}</p></div>
          ))}
        </div>
      </div>
      <div className="relative hidden lg:col-span-5 lg:block">
        <img src={HERO_IMG} alt="Feria de empleo" className="h-full max-h-[520px] w-full rounded-[32px] object-cover" />
        <div className="absolute -left-8 bottom-10 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-xl backdrop-blur">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-upv">{t("Una identidad")}</p>
          <p className="mt-1 font-display text-lg font-bold">{t("Un QR para todo el evento")}</p>
        </div>
      </div>
    </div>
  </section>
  );
};

export const FormSection = ({ n, title, children, testId }) => (
  <section className="border-b border-slate-100 py-8 last:border-0" data-testid={testId}>
    <div className="mb-6 flex items-center gap-3">
      <span className="font-mono text-xs font-bold text-upv">0{n}</span>
      <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
    </div>
    {children}
  </section>
);

export const Field = ({ id, label, value, onChange, error, optional, ...rest }) => {
  const { t } = useI18n();
  return (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-600">
      {t(label)} {optional && <span className="normal-case tracking-normal text-slate-400">{t("(opcional)")}</span>}
    </Label>
    <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className={`h-12 rounded-xl ${error ? "border-rose-400" : ""}`} data-testid={`registro-input-${id}`} {...rest} />
    {error && <p className="text-xs font-medium text-rose-600" data-testid={`registro-error-${id}`}>{t(error)}</p>}
  </div>
  );
};

export const CvUpload = ({ cv, onFile, error }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const { t } = useI18n();
  const handle = (file) => file && onFile(file);

  if (cv) {
    return (
      <div className="flex flex-col gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="registro-cv-uploaded">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-upv shadow-sm"><FileText className="h-6 w-6" /></span>
          <div>
            <p className="font-mono text-sm font-semibold text-slate-900" data-testid="registro-cv-filename">{cv.name}</p>
            <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {t("Currículum cargado correctamente")}</p>
          </div>
        </div>
        <button type="button" onClick={() => input.current?.click()} className="text-sm font-semibold text-slate-600 underline-offset-4 hover:underline" data-testid="registro-cv-change-button">{t("Cambiar archivo")}</button>
        <input ref={input} type="file" accept="application/pdf" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
        className={`flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-upv bg-upv-light" : error ? "border-rose-300 bg-rose-50/50" : "border-slate-300 bg-slate-50 hover:border-upv"}`}
        data-testid="registro-cv-dropzone"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-upv shadow-sm"><Upload className="h-5 w-5" /></span>
        <span className="font-display text-base font-bold tracking-wider">{t("SUBE TU CURRÍCULUM")}</span>
        <span className="text-xs text-slate-500">{t("Formato PDF · arrastra el archivo o haz clic")}</span>
      </button>
      <input ref={input} type="file" accept="application/pdf" className="hidden" onChange={(e) => handle(e.target.files?.[0])} data-testid="registro-cv-file-input" />
      {error && <p className="mt-2 text-xs font-medium text-rose-600" data-testid="registro-error-cv">{t(error)}</p>}
    </div>
  );
};

export const ConsentBox = ({ checked, onChange, error }) => {
  const { t } = useI18n();
  return (
  <div className={`rounded-2xl border p-6 ${error ? "border-rose-300" : "border-slate-200"} bg-white`} data-testid="registro-consent-box">
    <div className="flex items-start gap-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-upv-light text-upv"><ShieldCheck className="h-5 w-5" /></span>
      <div className="space-y-3 text-sm leading-relaxed text-slate-600">
        <p>{t("Durante el Foro podrás compartir voluntariamente tu perfil profesional y currículum con las empresas participantes.")}</p>
        <p>{t("Cuando muestres tu código QR a una empresa para que sea escaneado, estarás solicitando compartir tu perfil y currículum con esa empresa.")}</p>
        <p className="text-xs text-slate-400">{t("Tu QR solo contiene un identificador seguro. Nunca incluye tu nombre, email, teléfono ni tu CV.")}</p>
      </div>
    </div>
    <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl bg-slate-50 p-4">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5 h-5 w-5" data-testid="registro-consent-checkbox" />
      <span className="text-sm font-semibold text-slate-800">{t("He leído y acepto las condiciones de participación y el tratamiento de mis datos.")}</span>
    </label>
    {error && <p className="mt-2 text-xs font-medium text-rose-600" data-testid="registro-error-consent">{t(error)}</p>}
  </div>
  );
};
